import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  StarIcon,
  CheckCircleIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';

interface Survey {
  id: string;
  number: string;
  shortDescription: string;
  description?: string | null;
  priority: string;
  state: string;
  category?: string | null;
  callerId: string;
  assignedToId?: string | null;
  templateId: string;
  respondentId?: string | null;
  respondentEmail: string;
  score?: number | null;
  completedAt?: string | null;
  sentAt?: string | null;
  expiresAt?: string | null;
  token?: string | null;
  slaTarget?: string | null;
  slaBreached: boolean;
  slaBreachTime?: string | null;
  closedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdById?: string | null;
  updatedById?: string | null;
  SurveyTemplate?: {
    id: string;
    name: string;
    description?: string | null;
    surveyType: string;
    active: boolean;
    createdAt: string;
    updatedAt: string;
  } | null;
  User_Survey_respondentIdToUser?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    displayName?: string | null;
    Customer?: {
      id: string;
      accountName: string;
    } | null;
  } | null;
  ticketPriority?: string | null;
  /** Customer/Company name (from respondent or linked case) - used for list display */
  customerAccountName?: string | null;
}

const priorityColors: Record<string, string> = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200'
};

const priorityLabels: Record<string, string> = {
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning'
};

const stateIcons: Record<string, React.ReactNode> = {
  new: <DocumentTextIcon className="h-5 w-5 text-gray-500" />,
  sent: <PaperAirplaneIcon className="h-5 w-5 text-blue-500" />,
  in_progress: <DocumentTextIcon className="h-5 w-5 text-yellow-500" />,
  completed: <CheckCircleIcon className="h-5 w-5 text-green-500" />,
  expired: <DocumentTextIcon className="h-5 w-5 text-gray-500" />,
  cancelled: <DocumentTextIcon className="h-5 w-5 text-red-500" />
};

const stateColors: Record<string, string> = {
  new: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  expired: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800'
};

const stateLabels: Record<string, string> = {
  new: 'New',
  sent: 'Sent',
  in_progress: 'In Progress',
  completed: 'Completed',
  expired: 'Expired',
  cancelled: 'Cancelled'
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const PRIORITY_FILTER_OPTIONS = [
  { value: 'critical_1', label: 'Critical' },
  { value: 'high_2', label: 'High' },
  { value: 'moderate_3', label: 'Moderate' },
  { value: 'low_4', label: 'Low' },
  { value: 'planning_5', label: 'Planning' },
];

const STATE_FILTER_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'sent', label: 'Sent' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Helper function to strip HTML tags and decode HTML entities
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const SurveyList: React.FC = () => {
  const { formatDate, preferences } = useDateFormatter();
  const [searchParams, setSearchParams] = useSearchParams();

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearch = searchParams.get('search') ?? '';
  const filterSurvey = searchParams.get('survey') ?? '';
  const filterPriority = searchParams.get('priority') ?? '';
  const filterCompany = searchParams.get('company') ?? '';
  const filterRespondent = searchParams.get('respondent') ?? '';
  const filterState = searchParams.get('state') ?? '';

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  // Custom formatter that returns date and time separately for display
  const formatDateTimeWithAMPM = (dateInput: string | Date | null | undefined): { date: string; time: string } => {
    if (!dateInput) return { date: '-', time: '' };
    
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return { date: '-', time: '' };
    
    try {
      // Format date part using user preferences
      const datePart = formatDate(dateInput);
      
      // Format time part with AM/PM (force 12-hour format)
      const timeOptions: Intl.DateTimeFormatOptions = {
        timeZone: preferences.timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true, // Force 12-hour format with AM/PM
      };
      
      const timePart = date.toLocaleTimeString('en-US', timeOptions);
      
      return { date: datePart, time: timePart };
    } catch (error) {
      console.error('Date formatting error:', error);
      return { date: date.toISOString(), time: '' };
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['surveys', page, pageSize, appliedSearch, filterSurvey, filterState, filterPriority, filterCompany, filterRespondent],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
      });
      if (appliedSearch) params.set('search', appliedSearch);
      if (filterSurvey) params.set('survey', filterSurvey);
      if (filterState) params.set('state', filterState);
      if (filterPriority) params.set('priority', filterPriority);
      if (filterCompany) params.set('company', filterCompany);
      if (filterRespondent) params.set('respondent', filterRespondent);

      const response = await api.get(`/surveys?${params}`);
      return response.data;
    },
    refetchOnMount: true,
    staleTime: 0,
  });

  const { data: statsData } = useQuery({
    queryKey: ['survey-stats'],
    queryFn: async () => {
      const response = await api.get('/surveys/stats');
      return response.data;
    },
    refetchOnMount: true,
    staleTime: 0,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({ search: searchInput.trim() || null, page: '1' });
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value === '') {
      patchSearchParams({ search: null, page: '1' });
    }
  };

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'survey':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterSurvey}
              placeholder="Filter survey..."
              onChange={(v) => {
                patchSearchParams({ survey: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'priority':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={PRIORITY_FILTER_OPTIONS}
              value={filterPriority}
              placeholder="All Priorities"
              onChange={(v) => {
                patchSearchParams({ priority: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'company':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterCompany}
              placeholder="Filter company..."
              onChange={(v) => {
                patchSearchParams({ company: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'respondent':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterRespondent}
              placeholder="Filter respondent..."
              onChange={(v) => {
                patchSearchParams({ respondent: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'state':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={STATE_FILTER_OPTIONS}
              value={filterState}
              placeholder="All States"
              onChange={(v) => {
                patchSearchParams({ state: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [filterSurvey, filterPriority, filterCompany, filterRespondent, filterState, patchSearchParams]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearch) {
      chips.push({ id: 'search', label: 'Search', value: `"${appliedSearch}"`, color: 'blue' });
    }
    if (filterSurvey) {
      chips.push({ id: 'survey', label: 'Survey', value: filterSurvey, color: 'blue' });
    }
    if (filterPriority) {
      const label = priorityLabels[filterPriority] || filterPriority;
      chips.push({ id: 'priority', label: 'Ticket Priority', value: label, color: 'orange' });
    }
    if (filterCompany) {
      chips.push({ id: 'company', label: 'Company', value: filterCompany, color: 'teal' });
    }
    if (filterRespondent) {
      chips.push({ id: 'respondent', label: 'Respondent', value: filterRespondent, color: 'cyan' });
    }
    if (filterState) {
      const label = stateLabels[filterState] || filterState;
      chips.push({ id: 'state', label: 'State', value: label, color: 'purple' });
    }
    return chips;
  }, [appliedSearch, filterSurvey, filterPriority, filterCompany, filterRespondent, filterState]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'survey':
        patchSearchParams({ survey: null, page: '1' });
        break;
      case 'priority':
        patchSearchParams({ priority: null, page: '1' });
        break;
      case 'company':
        patchSearchParams({ company: null, page: '1' });
        break;
      case 'respondent':
        patchSearchParams({ respondent: null, page: '1' });
        break;
      case 'state':
        patchSearchParams({ state: null, page: '1' });
        break;
    }
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      survey: null,
      priority: null,
      company: null,
      respondent: null,
      state: null,
      page: '1',
    });
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <StarSolidIcon key={i} className="h-4 w-4 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative">
            <StarIcon className="h-4 w-4 text-gray-300" />
            <StarSolidIcon className="h-4 w-4 text-yellow-400 absolute top-0 left-0" style={{ clipPath: 'inset(0 50% 0 0)' }} />
          </div>
        );
      } else {
        stars.push(
          <StarIcon key={i} className="h-4 w-4 text-gray-300" />
        );
      }
    }
    return stars;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading surveys. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Surveys</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage customer satisfaction surveys and feedback
            </p>
          </div>
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                value={searchInput}
                onChange={handleSearchInputChange}
                placeholder="Search surveys..."
                className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Search
            </button>
          </form>
        </div>
      </div>

      {/* Stats Cards */}
      {statsData && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardDocumentCheckIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-2 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Surveys</dt>
                    <dd className="text-lg font-medium text-gray-900">{statsData.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <PaperAirplaneIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-2 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Sent</dt>
                    <dd className="text-lg font-medium text-gray-900">{statsData.sent}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-2 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                    <dd className="text-lg font-medium text-gray-900">{statsData.completed || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-2 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Expired</dt>
                    <dd className="text-lg font-medium text-gray-900">{statsData.expired || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-2 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Response Rate</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statsData.responseRate !== undefined && statsData.responseRate !== null && !isNaN(statsData.responseRate) 
                        ? `${statsData.responseRate.toFixed(1)}%` 
                        : '0.0%'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <StarIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-2 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Avg Satisfaction</dt>
                    <dd className="flex flex-col items-start space-y-1">
                      {statsData.avgSatisfaction ? (
                        <>
                          <span className="text-lg font-medium text-gray-900">
                            {statsData.avgSatisfaction.toFixed(2)}/5
                          </span>
                          <div className="flex items-center space-x-0.5">
                            {renderStars(statsData.avgSatisfaction)}
                          </div>
                        </>
                      ) : (
                        <span className="text-sm text-gray-500">N/A</span>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeFilterChips.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-4 px-4 py-2">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      {/* Surveys Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('survey', 'Survey')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('priority', 'Ticket Priority')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('company', 'Company Name')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('respondent', 'Respondent')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('state', 'State')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Score
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.surveys?.map((survey: Survey) => (
              <tr key={survey.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <Link
                      to={`/surveys/${survey.id}`}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                    >
                      {survey.number}
                    </Link>
                    <div className="text-sm text-gray-500">{stripHtml(survey.shortDescription)}</div>
                    {(() => {
                      const dateTime = survey.sentAt ? formatDateTimeWithAMPM(survey.sentAt) : formatDateTimeWithAMPM(survey.createdAt);
                      return (
                        <div className="flex flex-col mt-1">
                          <span className="text-xs text-gray-400">{dateTime.date} {dateTime.time}</span>
                        </div>
                      );
                    })()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {survey.ticketPriority ? (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[survey.ticketPriority] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                      {priorityLabels[survey.ticketPriority] || survey.ticketPriority}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {survey.customerAccountName ?? survey.User_Survey_respondentIdToUser?.Customer?.accountName ?? '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {survey.User_Survey_respondentIdToUser ? (
                    <div>
                      <div className="text-gray-900">
                        {survey.User_Survey_respondentIdToUser.displayName || 
                         `${survey.User_Survey_respondentIdToUser.firstName} ${survey.User_Survey_respondentIdToUser.lastName}`}
                      </div>
                      <div className="text-gray-500 text-xs">{survey.User_Survey_respondentIdToUser.email}</div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-gray-500 text-xs">{survey.respondentEmail}</div>
                      <div className="text-gray-400 text-xs">No user account</div>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {stateIcons[survey.state] || <DocumentTextIcon className="h-5 w-5 text-gray-500" />}
                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stateColors[survey.state] || 'bg-gray-100 text-gray-800'}`}>
                      {stateLabels[survey.state] || survey.state}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {survey.state === 'completed' && survey.score !== undefined && survey.score !== null ? (
                    <div className="flex flex-col items-start space-y-1">
                      <span className="text-sm font-medium text-gray-900">{survey.score.toFixed(2)}/5</span>
                      <div className="flex items-center space-x-0.5">
                        {renderStars(survey.score)}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {(!data?.surveys || data.surveys.length === 0) && (
          <div className="text-center py-12">
            <p className="text-gray-500">No surveys found</p>
          </div>
        )}

        {/* Enhanced Pagination */}
        {!isLoading && data?.surveys && data.surveys.length > 0 && data.totalPages > 0 && (
          <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex-1 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label htmlFor="pageSize" className="text-sm text-gray-700">
                    Show
                  </label>
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={(e) => {
                      patchSearchParams({ pageSize: e.target.value, page: '1' });
                    }}
                    className="border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>

                <div className="text-sm text-gray-700">
                  Showing {data.total === 0 ? 0 : (page - 1) * pageSize + 1} to {Math.min(page * pageSize, data.total)} of {data.total} entries
                </div>
              </div>

              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => patchSearchParams({ page: '1' })}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">First</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M15.79 14.77a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L11.832 10l3.938 3.71a.75.75 0 01.02 1.06zm-6 0a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L5.832 10l3.938 3.71a.75.75 0 01.02 1.06z" clipRule="evenodd" />
                    </svg>
                  </button>

                  <button
                    onClick={() => patchSearchParams({ page: String(Math.max(1, page - 1)) })}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {(() => {
                    const totalPages = data.totalPages;
                    const pages: (number | string)[] = [];
                    const maxVisible = 5;

                    if (totalPages <= maxVisible + 2) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      pages.push(1);

                      if (page > 3) {
                        pages.push('...');
                      }

                      const start = Math.max(2, page - 1);
                      const end = Math.min(totalPages - 1, page + 1);

                      for (let i = start; i <= end; i++) {
                        pages.push(i);
                      }

                      if (page < totalPages - 2) {
                        pages.push('...');
                      }

                      pages.push(totalPages);
                    }

                    return pages.map((pageNum, index) => (
                      <React.Fragment key={index}>
                        {pageNum === '...' ? (
                          <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        ) : (
                          <button
                            onClick={() => patchSearchParams({ page: String(pageNum) })}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === pageNum
                                ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        )}
                      </React.Fragment>
                    ));
                  })()}

                  <button
                    onClick={() => patchSearchParams({ page: String(Math.min(data.totalPages, page + 1)) })}
                    disabled={page === data.totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>

                  <button
                    onClick={() => patchSearchParams({ page: String(data.totalPages) })}
                    disabled={page === data.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Last</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02zm-6 0a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyList;
